import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, of, switchMap, take } from 'rxjs';
import { Config } from 'src/app/shared/models';
import { ConfigService, CurrentFacilityService } from 'src/app/shared/services';
import { Organization } from 'src/app/shared/services/firebase/models/organizations';
import { OrganizationService } from 'src/app/shared/services/firebase/organizations.service';
import { configFacility } from 'src/environments/main-module';
import { DBConfigService } from './config.service';

@Injectable({
    providedIn: 'root',
})
export class AppInitService {
    private isLoadingSubject = new BehaviorSubject<boolean>(true);
    public isLoading$ = this.isLoadingSubject.asObservable();

    constructor(
        private configService: ConfigService,
        private dbConfigService: DBConfigService,
        private organizationService: OrganizationService,
        private currentFacilityService: CurrentFacilityService
    ) {}

    initializeApp(): Observable<void> {
        this.isLoadingSubject.next(true);
        return this.dbConfigService.getConfig().pipe(
            take(1),
            switchMap((config) => {
                if (config && config.orgId) {
                    return this.organizationService.getOrganizationById(config.orgId).pipe(
                        catchError(() => {
                            return of(undefined);
                        })
                    );
                }

                return of(undefined);
            }),
            map((orgData: Organization | undefined) => {
                if (!!orgData) {
                    this.setInitialConfiguration(orgData ?? {});
                }
                this.setGlobalConfig(orgData ?? {});
                if (orgData?.appUrl) {
                    this.redirectToMainHost(orgData?.appUrl);
                }
                this.isLoadingSubject.next(false);
            }),
            catchError(() => {
                this.isLoadingSubject.next(false);
                return of();
            })
        );
    }

    private setGlobalConfig(dbConfig: Organization): void {
        const config = new configFacility();
        const globalConfig = Config.fromJson({
            ...config,
            ...dbConfig?.features?.searchForm,
            ...dbConfig,
            ...dbConfig?.features,
        });

        this.configService.setConfig(globalConfig);
    }

    private setInitialConfiguration(dbConfig: Organization): void {
        this.setCustomerServiceInfo(dbConfig);
        this.setInitConfig(dbConfig);
    }

    private setCustomerServiceInfo(dbConfig: Organization): void {
        this.currentFacilityService.setCustomerServiceInfo({
            nameSection: `${dbConfig?.organizationName} Customer Service`,
            email: dbConfig?.contactEmail || '',
            phone: dbConfig?.phone || '',
            timeZone: dbConfig?.workingScheduleForm?.timeZone,
            operatingHours: this.currentFacilityService.getOperatingHours(dbConfig.workingScheduleForm) || undefined,
        });
    }

    private setInitConfig(dbConfig: Organization): void {
        this.currentFacilityService.setInitialConfiguration({
            indexTitle: dbConfig?.organizationName || 'Facility',
            urlFavIcon: dbConfig?.favIconURL || 'sprintpay-favicon.png',
            alternativeLogoLightUrl: dbConfig?.alternativeLogoLightUrl || 'sprintpay/img/sprintpay-logo-light.svg',
            alternativeLogoDarkUrl: dbConfig?.alternativeLogoDarkUrl || 'sprintpay/img/sprintpay-logo-dark.svg',
            alternativeLogoWidth: dbConfig?.alternativeLogoWidth || 'auto',
            alternativeLogoHeight: dbConfig?.alternativeLogoHeight || 'auto',
            mainLogoLightUrl: dbConfig?.mainLogoLightUrl || 'sprintpay/img/sprintpay-logo-light.svg',
            mainLogoDarkUrl: dbConfig?.mainLogoDarkUrl || 'sprintpay/img/sprintpay-logo-dark.svg',
            mainLogoWidth: dbConfig?.mainLogoWidth || 'auto',
            mainLogoHeight: dbConfig?.mainLogoHeight || 'auto',
            setMainLogoToHeader: dbConfig?.setMainLogoToHeader ?? false,
            setMainLogoToLogin: dbConfig?.setMainLogoToLogin ?? false,
            setAlternativeLogoToHeader: dbConfig?.setAlternativeLogoToHeader ?? false,
            setAlternativeLogoToLogin: dbConfig?.setAlternativeLogoToLogin ?? false,
            urlAdministrative: '',
            name: dbConfig?.organizationName || 'Facility',
            email: dbConfig?.email || 'support@cargosprint.com',
            termsOfUseURL: dbConfig?.termsUrl || 'https://cargosprint.com/legal/terms-of-use/#sprintpay',
            privacyAndPolicyURL: dbConfig?.privacyUrl || '',
            faqExternalURL: {
                externalURL: dbConfig?.faqURL || '',
                pdfURL: dbConfig?.pdfURL || '',
            },
            paidToFacilityId: dbConfig?.paidToFacilityId,
            facilitySource: dbConfig?.features?.searchForm?.facilitySource ?? '',
        });
    }

    private redirectToMainHost(host: string): void {
        const currentHost = window.location.hostname;
        const targetHost = host;
        const isInIframe = window.self !== window.top;

        try {
            if (!targetHost) return;

            const url = new URL(targetHost);

            const isFirebaseHosting = currentHost.endsWith('firebaseapp.com') || currentHost.endsWith('web.app');

            const isDifferentHost = currentHost !== url.hostname;

            if (!isInIframe && !!targetHost && isFirebaseHosting && isDifferentHost) {
                window.location.href = url.href;
            }
        } catch (err) {
            //eslint-disable-next-line no-console
            console.warn('Redirect canceled: invalid or malformed URL', err);
        }
    }
}
